import { MouseEvent, useCallback, useLayoutEffect, useRef } from 'react';
import { useI18nContext } from '@i18n/i18n-react';
import { Entity, EntityRelationType, EntityType } from '@common/api';
import { Breadcrumbs } from '@components/breadcrumbs/Breadcrumbs';
import { Button } from '@components/button';
import { ContextMenuItem } from '@components/context-menu/ContextMenu.style';
import { Edit, Folder } from '@components/icons/FarseerIcons';
import { FlexRow, FlexCol } from '@components/layout/Flex';
import { DividerVertical } from '@components/layout/Divider';
import { useModal } from '@components/modal/ModalProvider';
import { Text } from '@components/typography/Typography';
import { TooltipWrapper } from '@components/tooltip/Tooltip';
import { useFolderNavigateToEntity } from '@features/entity/entityFolder';
import { useDisplayPath } from '@data/entity/displayPath';
import { useEntityKeys } from '@data/entity/entity';
import { PseudoEntity, PseudoEntityType } from '@data/entity/types';
import { toRealEntity } from '@data/entity/util';
import { ExpViewer } from '@features/expression/ExpEditor';
import { ContextMenuTag } from '@features/tags/ContextMenuTag';
import { useTagModalSelect } from '@features/tags/tagModalSelect';
import { Tags } from '@features/tags/Tags';
import { useIsTyping } from '@hooks/useIsTyping';
import { ENTITY_ICONS, getEntityTypeSingular } from '@util/folderItems';

import { TypeaheadContainer, TypeaheadDetails, TypeaheadFilterLabel } from './Typeahead.style';

export type TypeaheadProps = {
    items: PseudoEntity[];

    selectedIndex: number;
    onSelect: (index: number) => void;
    onComplete: (item: PseudoEntity) => void;
};

const TAG_TYPES: Set<PseudoEntityType | undefined> = new Set([
    EntityType.Variable,
    EntityType.DimensionTable,
    EntityType.DimensionMember,
]);

const preventDefault = (e: MouseEvent) => e.preventDefault();

export const Typeahead = (props: TypeaheadProps) => {
    const { items, selectedIndex, onSelect, onComplete } = props;

    const { LL } = useI18nContext();
    const handleTagSelect = useTagModalSelect();
    const isTyping = useIsTyping();
    const { hideModal } = useModal();

    const typeaheadListRef = useRef<HTMLDivElement>(null);

    const info = LL.SEARCH_FOR_EXPRESSIONS();

    const activeItem = items?.[selectedIndex];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { type, meta, help, calculation, variable } = activeItem ?? ({} as any);

    const entityItem = activeItem && toRealEntity(activeItem);
    const { segments, sharedWithMe } = useDisplayPath(entityItem, [
        EntityRelationType.Parent,
        EntityRelationType.Folder,
    ]);
    const crumbs = [
        ...(sharedWithMe ? [{ label: LL.SHARED_WITH_ME() }] : []),
        ...segments.map((path) => ({ label: path.meta?.name ?? '' })),
    ];
    const breadcrumbs = crumbs.length ? crumbs : null;

    const tableKeys = useEntityKeys(EntityType.DimensionTable, variable?.tableGids);
    const memberKeys = useEntityKeys(EntityType.DimensionMember, variable?.memberGids);
    const tagKeys = [...tableKeys, ...memberKeys];

    useLayoutEffect(() => {
        const { current: typeaheadList } = typeaheadListRef;
        if (!typeaheadList) return;

        const active = typeaheadList.querySelector('.active');
        if (!active) return;

        let { top: min, bottom: max } = typeaheadList.getBoundingClientRect();
        const { top, bottom, height } = active.getBoundingClientRect();

        // Keep half item visible
        min += height / 2;
        max -= height / 2;

        if (top < min) typeaheadList.scrollTop += top - min;
        if (bottom > max) typeaheadList.scrollTop += bottom - max;
    }, [selectedIndex]);

    const navigateToEntity = useFolderNavigateToEntity();

    // To allow moving the mouse from the list to the details panel without losing focus,
    // ignore mouse motions within a +63º / -63º angle to the right: (atan(2) = ~63º)
    //
    //      .
    //     .
    //    .   ignored
    //     .
    //      .

    const lastMouseRef = useRef([1e5, 1e5]);
    const handleMouseMove = useCallback(
        (e: MouseEvent, index: number) => {
            const {
                current: [lastX, lastY],
            } = lastMouseRef;
            const { clientX: x, clientY: y } = e;
            lastMouseRef.current = [x, y];

            const dx = x - lastX;
            const dy = y - lastY;
            if (dx > Math.abs(dy) / 2) return;

            onSelect(index);
        },
        [onSelect]
    );

    return (
        <TypeaheadContainer onMouseDown={preventDefault}>
            <FlexCol style={{ width: 330, flexBasis: 0 }} $flexGrow>
                <TypeaheadFilterLabel>{info}</TypeaheadFilterLabel>
                <FlexCol $flexGrow={1} style={{ overflowY: 'auto' }} ref={typeaheadListRef}>
                    {items.map((item, idx) => (
                        <ContextMenuItem
                            key={`${item.type}-${item.id}-${idx}`}
                            onMouseMove={(e) => handleMouseMove(e, idx)}
                            onMouseDown={() => onComplete(items[idx])}
                            $textOnly={false}
                            $canHover={false}
                            $isHovered={idx === selectedIndex}
                            className={isTyping && idx === selectedIndex ? 'active' : undefined}
                        >
                            <ContextMenuTag entity={item} />
                        </ContextMenuItem>
                    ))}
                </FlexCol>
            </FlexCol>
            {activeItem ? (
                <>
                    <DividerVertical $padding={4} style={{ alignSelf: 'stretch', height: 'auto' }} />
                    <TypeaheadDetails style={{ width: 380 }}>
                        <FlexCol $gap={8}>
                            <FlexCol $gap={4}>
                                <FlexRow $spaceBetween>
                                    <FlexRow $align="center" style={{ marginLeft: -4 }}>
                                        {ENTITY_ICONS[type]}
                                        <Text $bold>{meta?.name}</Text>
                                    </FlexRow>

                                    <FlexRow $align="center" $justify="end" $gap={5}>
                                        {TAG_TYPES.has(type) ? (
                                            <>
                                                <TooltipWrapper
                                                    label={LL.OPEN_IN_EDITOR({
                                                        type:
                                                            type === EntityType.Variable
                                                                ? LL.VARIABLE()
                                                                : LL.DIMENSION(),
                                                    })}
                                                >
                                                    <Button
                                                        icon={Folder}
                                                        $type="quaternary"
                                                        $size="small"
                                                        onMouseDown={() => {
                                                            if (entityItem) {
                                                                navigateToEntity(entityItem);
                                                                hideModal();
                                                            }
                                                        }}
                                                    />
                                                </TooltipWrapper>
                                                <TooltipWrapper
                                                    label={LL.EDIT_IN({
                                                        type:
                                                            type === EntityType.Variable
                                                                ? LL.VARIABLE()
                                                                : LL.DIMENSION(),
                                                    })}
                                                >
                                                    <Button
                                                        icon={Edit}
                                                        $type="quaternary"
                                                        $size="small"
                                                        onMouseDown={() => handleTagSelect(activeItem as Entity)}
                                                    />
                                                </TooltipWrapper>
                                            </>
                                        ) : null}
                                    </FlexRow>
                                </FlexRow>

                                <Text $muted>{getEntityTypeSingular(type, LL)}</Text>
                            </FlexCol>

                            {!!breadcrumbs?.length && (
                                <FlexRow $wrap $gap={4} $align="center">
                                    <Breadcrumbs items={breadcrumbs} small />
                                </FlexRow>
                            )}

                            {!!help?.example?.length && (
                                <FlexCol $gap={4}>
                                    <div className="info">{LL.EXAMPLE()}</div>
                                    {help.example.split('\n').map((example, idx) => (
                                        <div key={`example-${idx}`} className="example">
                                            <ExpViewer expression={example} allowFakeTags />
                                        </div>
                                    ))}
                                </FlexCol>
                            )}
                            {!!meta?.description?.length && (
                                <FlexCol $gap={4}>
                                    <div>{LL.DESCRIPTION()}</div>
                                    {meta.description.split('\n').map((desc, idx) => (
                                        <div key={`desc-${idx}`} className="description">
                                            <Text $muted>{desc}</Text>
                                        </div>
                                    ))}
                                </FlexCol>
                            )}
                            {!!tagKeys.length && (
                                <FlexCol $gap={4}>
                                    <div className="info">{LL.DIMENSIONS()}</div>
                                    <FlexRow $wrap $gap={4}>
                                        <Tags tags={tagKeys} onSelect={null} />
                                    </FlexRow>
                                </FlexCol>
                            )}
                            {!!calculation?.formula?.length && (
                                <FlexCol $gap={4}>
                                    <div className="info">{LL.FORMULA()}</div>
                                    <div className="expression">
                                        {calculation?.formula.length ? (
                                            <ExpViewer expression={calculation?.formula} />
                                        ) : (
                                            LL.NONE()
                                        )}
                                    </div>
                                </FlexCol>
                            )}
                        </FlexCol>
                    </TypeaheadDetails>
                </>
            ) : null}
        </TypeaheadContainer>
    );
};
